import React, { useEffect, useState } from 'react';
import { FmlxTextBox, FmlxTextInput, FmlxTooltip } from 'fmlx-common-ui';
import { ErrorIcon } from '@fmlx/icons';
import { HexColors } from 'constants/colors';
import { experiment } from 'components/main/utils';
import { getSuggestedRotatedScreenName } from 'components/Experiment/utils';
import { TreeNode } from 'utils/ExperimentUtils';
import { CustomCheckBox } from 'components/input';
import { IStyle } from 'Styles';

type RotateCanvasOnChange = (
  event: React.ChangeEvent<HTMLInputElement>,
  isChecked: boolean,
  setIsChecked: React.Dispatch<React.SetStateAction<boolean>>,
) => void;

interface RotateCanvasModalProps {
  id?: string;
  screenNames?: string[];
  onChange: RotateCanvasOnChange;
  onNameChange?: (name: string) => void;
}

const RotateCanvasModal = ({
  id,
  screenNames = [],
  onChange,
  onNameChange,
}: RotateCanvasModalProps) => {
  const [showMessage, setShowMessage] = useState(false);
  const [newScreenName, setNewScreenName] = useState('');
  const [isChecked, setIsChecked] = useState<boolean>(false);

  useEffect(() => {
    checkScreenName(screenNames.join(', '));
  }, [screenNames]);

  const checkScreenName = (name: string) => {
    const rotationSuffix = experiment.RotateExpName;
    const generatedName = `${name}${rotationSuffix}`;
    if (generatedName.length > TreeNode.TreeNodeNameMaxLength) {
      const adjustedName = getSuggestedRotatedScreenName(name);
      setNewScreenName(adjustedName);
      onNameChange?.(adjustedName);
      setShowMessage(true);
      setIsChecked(false);
    } else {
      setNewScreenName(generatedName);
      onNameChange?.(generatedName);
      setShowMessage(false);
    }
  };

  const handleRefCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e, isChecked, setIsChecked);
  };

  return (
    <>
      <div id={id}>
        <div>
          Please note that this rotated screen will have to be manually repaired if there
          are any changes or fixes in the original screen.
        </div>

        {!showMessage && (
          <>
            <div className="my-2">
              <CustomCheckBox
                onChange={handleRefCheck}
                id="rotatecanvasmodal-labels"
                label="Don't show me again"
                checked={isChecked}
                mainContainerStyle={styles.rotateCanvasMessageContainer}
                labelStyle={styles.rotateCanvasMessageLabel}
                disabled={false}
              />
            </div>
          </>
        )}
        {showMessage && (
          <>
            <div>
              <p className="flex items-center gap-2 font-medium my-2">
                New Screen Name
                <FmlxTooltip
                  title={`Original Screen Name: ${screenNames.join(', ')}`}
                  placement={FmlxTooltip.Placement.TOP}
                >
                  <span className="inline-flex cursor-pointer items-center">
                    <ErrorIcon size="xs" customColor={HexColors.orange[1]} />
                  </span>
                </FmlxTooltip>
              </p>
            </div>
            <div className="my-4">
              <FmlxTextBox
                mode={FmlxTextInput.Mode.TEXT}
                value={newScreenName}
                onChange={(event) => {
                  setNewScreenName(event.value);
                  onNameChange?.(event.value);
                }}
                maxLength={TreeNode.TreeNodeNameMaxLength}
                disabled={false}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

const styles: IStyle = {
  rotateCanvasMessageContainer: {
    marginLeft: '0.5rem',
  },
  rotateCanvasMessageLabel: {
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontWeight: 600,
    marginLeft: '0.75rem',
  },
};

export default RotateCanvasModal;
